import { useState } from 'react'
import Logo from "../../assets/images/logo.png"
import banner from "../../assets/images/banner.jpeg";
import { FormInput, FormSelect, Button, PasswordChecker } from "components";
import { validateLogin, validateRegister } from 'components/misc/functions';
import { useDispatch } from 'react-redux';
import { show } from 'providers/toastSlice';
import { Link, useNavigate } from 'react-router-dom';
import { postRequest } from 'https/requests';
// `formatName` is a helper, not data — stays local.
import { formatName } from 'components/misc/constants';
import useConfigOptions from 'hooks/useConfigOptions';
import { STAGING_AUTH } from '../../config/env';

function Register() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { educationOptions, genderOptions, nationalityOptions, networkOptions } = useConfigOptions();
    const [otherNames, setOtherNames] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [pob, setPob] = useState('');
    const [address, setAddress] = useState('');
    const [nearestLandmark, setNearestLandmark] = useState('');
    const [postGPS, setPostGPS] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [education, setEducation] = useState('');
    const [gender, setGender] = useState('');
    const [phone, setPhone] = useState('');
    const [dob, setDob] = useState('');
    const [network, setNetwork] = useState('');
    const [nationality, setNationality] = useState('');
    const [strength, setStrength] = useState(null); 
    const [processing, setProcessing] = useState(false);

    const handleRequest = async(e) => {
        e.preventDefault();
        setProcessing(true);

        if(password !== passwordConfirm){
            dispatch(show({
                state : true,
                message : `Passwords do not match`,
                type : "error"
            }))
            setProcessing(false);
            return;
        }

        const errors = validateLogin(phone, password);
        for (const [key, value] of Object.entries(errors)) {
            dispatch(show({
                state : true,
                message : `${value}`,
                type : "error"
            }))
            return;
        }

        const otherErrors = validateRegister(otherNames, surname, email, pob, address, nearestLandmark, postGPS, education, dob, nationality, password);
        for (const [key, value] of Object.entries(otherErrors)) {
            dispatch(show({
                state : true,
                message : `${value}`,
                type : "error"
            }))
            return;
        }

        let data = { otherNames : formatName(otherNames), surname: formatName(surname), email, pob, address, nearestLandmark, postGPS, password, education, gender, phone, dob, network, nationality }

        let res = await postRequest(`${STAGING_AUTH}/register`, data);
        setProcessing(false);
        
        if(res?.err){
            dispatch(show({
                state : true,
                message : `${res?.err}`,
                type : "error"
            }))
            return;
        }

        dispatch(show({
            state : true,
            message : `${res?.message}`,
            type : "success"
        }))

        setTimeout(() => {
            setProcessing(false);
            navigate("/login")
        }, 3000);
    }

    return (
        <div className='min-h-screen w-full flex bg-light-bg dark:bg-dark-page relative overflow-hidden'>
            <div className='pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full bg-light-blue/10 blur-3xl'></div>
            <div className='pointer-events-none absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-logo-red/10 blur-3xl'></div>

            <div className='w-full lg:w-1/2 flex flex-col items-center py-10 px-4 max-h-screen overflow-y-auto relative z-10'>
                <div className='w-full max-w-2xl'>
                    <img src={Logo} alt='Cedispay' className='h-11 w-auto mx-auto mb-8' />

                    <div className='bg-white dark:bg-dark-bg rounded-2xl p-8 md:p-10 shadow-xl shadow-slate-200/60 dark:shadow-black/30 border border-slate-100 dark:border-slate-700'>
                        <div className='mb-7 text-center'>
                            <h1 className='text-2xl md:text-3xl font-semibold text-heading dark:text-slate-100 tracking-tight'>
                                Create your account
                            </h1>
                            <p className='mt-2 text-sm text-slate-500 dark:text-slate-400'>
                                Join Cedispay in just a few steps.
                            </p>
                        </div>

                        <form id="admin-login" onSubmit={handleRequest} className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                        <FormInput 
                            label='First name'
                            type='text'
                            name='firstname'
                            id='firstname'
                            placeholder='Enter your first name'
                            value={otherNames}
                            setValue={setOtherNames}
                            required={true}
                        />
                        
                        <FormInput 
                            label='Surname'
                            type='text'
                            name='surname'
                            id='surname'
                            placeholder='Enter your surname'
                            value={surname}
                            setValue={setSurname}
                            required={true}
                        />
                        
                        <FormInput 
                            label='Email'
                            type='email'
                            name='email'
                            id='email'
                            placeholder='Enter your email address'
                            value={email}
                            setValue={setEmail}
                            required={true}
                        />
                        
                        <FormInput 
                            label='Phone Number'
                            inputMode='numeric'
                            pattern='[0-9]*'
                            name='phone'
                            id='phone'
                            placeholder='Enter your phone number'
                            value={phone}
                            setValue={setPhone}
                            required={true}
                        />

                        <FormSelect 
                            label='Mobile Network'
                            name='network'
                            id='network'
                            content={networkOptions}
                            value={network}
                            setValue={setNetwork}
                            required={true}
                        />
                        
                        <FormSelect 
                            label='Education'
                            name='education'
                            id='education'
                            content={educationOptions}
                            value={education}
                            setValue={setEducation}
                            required={true}
                        />
                        
                        <FormSelect 
                            label='Gender'
                            name='gender'
                            id='gender'
                            content={genderOptions}
                            value={gender}
                            setValue={setGender}
                            required={true}
                        />

                        <FormInput 
                            label='Date of birth'
                            type='date'
                            name='dob'
                            id='dob'
                            value={dob}
                            setValue={setDob}
                            required={true}
                        />

                        <FormInput 
                            label='Place of birth'
                            type='text'
                            name='pob'
                            id='pob'
                            placeholder='Enter your place of birth'
                            value={pob}
                            setValue={setPob}
                            required={true}
                        />

                        <FormSelect 
                            label='Nationality'
                            name='nationality'
                            id='nationality'
                            content={nationalityOptions}
                            value={nationality}
                            setValue={setNationality}
                            required={true}
                        />

                        <FormInput 
                            label='Home Address'
                            type='text'
                            name='address'
                            id='address'
                            placeholder='Enter your home address'
                            value={address}
                            setValue={setAddress}
                            required={true}
                        />

                        <FormInput 
                            label='Nearest Landmark'
                            type='text'
                            name='nearestLandmark'
                            id='nearestLandmark'
                            placeholder='Enter your home address'
                            value={nearestLandmark}
                            setValue={setNearestLandmark}
                            required={true}
                        />

                        <div className='col-span-full'>
                            <FormInput 
                                label='Digital Address'
                                type='text'
                                name='postGPS'
                                id='postGPS'
                                placeholder='Enter your GhanaPost GPS address eg. GA-543-1234'
                                value={postGPS}
                                setValue={setPostGPS}
                                required={true}
                                pattern="[A-Z]{2}-(\d{3}|\d{4})-\d{4}"
                                title="Please enter the input in the format AA-XXX-XXXX"
                            />
                        </div>

                        <FormInput 
                            label='Password'
                            type='password'
                            name='password'
                            id='password'
                            value={password}
                            setValue={setPassword}
                            required={true}
                        />

                        <FormInput 
                            label='Confirm Password'
                            type='password'
                            name='confirm_password'
                            id='confirm_password'
                            value={passwordConfirm}
                            setValue={setPasswordConfirm}
                            required={true}
                        />

                        <div className='col-span-full -mt-1'>
                        {password !== "" && (
                            <PasswordChecker 
                                password={password}
                                strength={strength}
                                setStrength={setStrength}
                            />
                        )}
                        </div>

                        <div className='col-span-full'>
                            <Button 
                                btnText={"Sign up"}
                                btnType={"submit"}
                                btnClasses={"form-button"}
                                processing={processing}
                            />
                        </div>
                        </form>
                    </div>

                    <p className='mt-6 mb-2 text-center text-sm text-slate-600 dark:text-slate-400'>
                        Already have an account?{' '}
                        <Link to="/login" className='text-light-blue hover:text-dark-blue font-semibold'>
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>

            <div className='hidden lg:block w-1/2 relative p-4 h-screen sticky top-0'>
                <div className='relative w-full h-full rounded-3xl overflow-hidden shadow-2xl'>
                    <img src={banner} alt="Cedispay" className='absolute inset-0 w-full h-full object-cover' />
                    <div className='absolute inset-0 bg-gradient-to-t from-dark-blue/80 via-dark-blue/20 to-transparent'></div>
                    <div className='absolute bottom-0 left-0 right-0 p-10 text-white'>
                        <h2 className='text-3xl font-semibold leading-tight max-w-md'>
                            Start your journey with Cedispay.
                        </h2>
                        <p className='mt-3 text-white/80 max-w-md'>
                            Get access to flexible loans designed to support your goals.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Register